import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import {
  Table,
  Segment,
  Container,
  Input,
  Header,
  Form,
  Icon,
  Button,
  Message,
  Label,
} from "semantic-ui-react";

import { Link } from "react-router-dom";

import {
  getDocs,
  collection,
  where,
  query,
} from "firebase/firestore";

import moment from "moment";

import Alertas from "./Alertas";
import ItemsSTDb from "../firebase/ItemsSTDb";
import ExportarExcel from "../funciones/ExportarExcel";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import { UserContext } from "../context/UserContext";

const ItemsTest = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  // =========================================================
  // ESTADOS
  // =========================================================

  const [estadoAlerta, cambiarEstadoAlerta] = useState(false);
  const [alerta, cambiarAlerta] = useState({});

  const [item, setItem] = useState("");
  const [categoria, setCategoria] = useState("");
  const [price, setPrice] = useState("");

  const [leer, setLeer] = useState([]);
  const [buscador, setBuscador] = useState("");
  const [flag, setFlag] = useState(false);
  const [botonDisabled, setBotonDisabled] = useState(false);

  /*
   * Categorías de Ítems ST obtenidas desde:
   *
   * catalogosEmpresa
   * catalogoId = "item_st"
   */
  const [categoriasST, setCategoriasST] = useState([]);
  const [cargandoCategorias, setCargandoCategorias] =
    useState(false);
  const [errorCategorias, setErrorCategorias] = useState("");

  // =========================================================
  // HELPERS
  // =========================================================

  const resetAlerta = () => {
    cambiarEstadoAlerta(false);
    cambiarAlerta({});
  };

  const mostrarError = (mensaje) => {
    cambiarEstadoAlerta(true);
    cambiarAlerta({
      tipo: "error",
      mensaje,
    });
  };

  const mostrarExito = (mensaje) => {
    cambiarEstadoAlerta(true);
    cambiarAlerta({
      tipo: "exito",
      mensaje,
    });
  };

  const volver = () => {
    window.history.back();
  };

  // =========================================================
  // CARGAR ITEMS ST
  // =========================================================

  const getData = async () => {
    if (!users?.emp_id) {
      setLeer([]);
      return;
    }

    try {
      const traerit = collection(
        db,
        "itemsst"
      );

      const dato = query(
        traerit,
        where(
          "emp_id",
          "==",
          users.emp_id
        )
      );

      const data = await getDocs(dato);

      const registros = data.docs.map(
        (documento) => ({
          ...documento.data(),
          id: documento.id,
        })
      );

      setLeer(registros);
    } catch (error) {
      console.error(
        "Error al cargar items ST:",
        error
      );

      mostrarError(
        "No fue posible cargar los Items ST."
      );
    }
  };

  // =========================================================
  // CARGAR CATEGORÍAS ITEMS ST
  // catalogosEmpresa / item_st
  // =========================================================

  const cargarCategoriasST = async () => {
    if (!users?.emp_id) {
      setCategoriasST([]);
      return;
    }

    setCargandoCategorias(true);
    setErrorCategorias("");

    try {
      const catalogosRef = collection(
        db,
        "catalogosEmpresa"
      );

      const q = query(
        catalogosRef,
        where(
          "emp_id",
          "==",
          users.emp_id
        ),
        where(
          "catalogoId",
          "==",
          "item_st"
        )
      );

      const snap = await getDocs(q);

      /*
       * Solamente utilizamos categorías activas.
       * El orden se realiza en cliente para evitar
       * un índice compuesto adicional.
       */
      const categorias = snap.docs
        .map((documento) => ({
          id: documento.id,
          ...documento.data(),
        }))
        .filter(
          (registro) =>
            registro.activo === true
        )
        .sort((a, b) => {
          const ordenA = Number(
            a.orden ?? 999
          );

          const ordenB = Number(
            b.orden ?? 999
          );

          if (ordenA !== ordenB) {
            return ordenA - ordenB;
          }

          return String(
            a.text || ""
          ).localeCompare(
            String(b.text || ""),
            "es",
            {
              sensitivity: "base",
            }
          );
        });

      setCategoriasST(categorias);
    } catch (error) {
      console.error(
        "Error al cargar Categorías Items ST:",
        error
      );

      setCategoriasST([]);

      setErrorCategorias(
        "No fue posible cargar las Categorías Ítems ST."
      );
    } finally {
      setCargandoCategorias(false);
    }
  };

  // =========================================================
  // CARGA INICIAL
  // =========================================================

  useEffect(() => {
    if (!users?.emp_id) return;

    getData();
    cargarCategoriasST();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users?.emp_id]);

  /*
   * Cuando agregamos un nuevo Item ST,
   * recargamos solamente los Items.
   */
  useEffect(() => {
    if (!users?.emp_id) return;

    getData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag]);

  // =========================================================
  // OPCIONES DROPDOWN
  // =========================================================

  const categoriaOptions = useMemo(() => {
    return categoriasST.map(
      (registro, index) => ({
        key:
          registro.itemId ||
          registro.key ||
          registro.id ||
          index,

        /*
         * Conservamos como value el valor definido
         * en el catálogo.
         */
        value:
          registro.value ||
          registro.text ||
          "",

        text:
          registro.text ||
          registro.value ||
          "",
      })
    );
  }, [categoriasST]);

  // =========================================================
  // ITEMS ORDENADOS
  // =========================================================

  const itemsOrdenados = useMemo(() => {
    return [...leer].sort((a, b) => {
      const categoriaCompare = String(
        a.categoria || ""
      ).localeCompare(
        String(b.categoria || ""),
        "es",
        {
          sensitivity: "base",
        }
      );

      if (categoriaCompare !== 0) {
        return categoriaCompare;
      }

      return String(
        a.nombre || ""
      ).localeCompare(
        String(b.nombre || ""),
        "es",
        {
          sensitivity: "base",
        }
      );
    });
  }, [leer]);

  // =========================================================
  // CAMBIO CATEGORÍA
  // =========================================================

  const handleDropdownChange = (
    e,
    { value }
  ) => {
    resetAlerta();

    setCategoria(value);

    /*
     * Si cambia desde REPUESTO/SERVICIO
     * a otra categoría, limpiamos precio.
     */
    if (
      value !== "REPUESTO" &&
      value !== "SERVICIO"
    ) {
      setPrice("");
    }
  };

  // =========================================================
  // FORMATEAR FECHA
  // =========================================================

  const formatearFecha = (fecha) => {
    if (!fecha) return "";

    if (fecha?.toDate) {
      return moment(
        fecha.toDate()
      ).format("DD/MM/YYYY HH:mm");
    }

    return String(fecha);
  };

  // =========================================================
  // FILTRO
  // =========================================================

  const filteredDataT = useMemo(() => {
    const textoBusqueda = String(
      buscador || ""
    )
      .trim()
      .toLowerCase();

    return itemsOrdenados.filter(
      (registro) => {
        const coincideTexto =
          !textoBusqueda ||
          Object.keys(registro).some(
            (key) => {
              let value =
                registro[key];

              if (
                [
                  "date",
                  "fecha_out",
                  "fechaadd",
                  "fechamod",
                ].includes(key)
              ) {
                value =
                  formatearFecha(value);
              }

              return String(
                value ?? ""
              )
                .toLowerCase()
                .includes(
                  textoBusqueda
                );
            }
          );

        /*
         * Conservamos el comportamiento actual:
         * la categoría seleccionada también
         * filtra el listado.
         */
        const coincideCategoria =
          !categoria ||
          registro.categoria ===
            categoria;

        return (
          coincideTexto &&
          coincideCategoria
        );
      }
    );
  }, [
    itemsOrdenados,
    buscador,
    categoria,
  ]);

  // =========================================================
  // EXPORTAR EXCEL
  // =========================================================

  const ExportarXls = () => {
    if (
      filteredDataT.length === 0
    ) {
      mostrarError(
        "No existen registros para exportar."
      );

      return;
    }

    const columnsToShow = [
      "id",
      "categoria",
      "nombre",
      "price",
    ];

    const dataOrdenada = [
      ...filteredDataT,
    ].sort((a, b) => {
      const categoriaCompare =
        String(
          a.categoria || ""
        ).localeCompare(
          String(
            b.categoria || ""
          ),
          "es",
          {
            sensitivity: "base",
          }
        );

      if (
        categoriaCompare !== 0
      ) {
        return categoriaCompare;
      }

      return String(
        a.nombre || ""
      ).localeCompare(
        String(
          b.nombre || ""
        ),
        "es",
        {
          sensitivity: "base",
        }
      );
    });

    const excelBlob =
      ExportarExcel(
        dataOrdenada,
        columnsToShow
      );

    const excelURL =
      URL.createObjectURL(
        excelBlob
      );

    const downloadLink =
      document.createElement("a");

    downloadLink.href =
      excelURL;

    downloadLink.download =
      "items.xlsx";

    downloadLink.click();

    URL.revokeObjectURL(
      excelURL
    );
  };

  // =========================================================
  // AGREGAR ITEM
  // =========================================================

  const handleSubmit = async (
    e
  ) => {
    e.preventDefault();

    resetAlerta();

    if (botonDisabled) {
      return;
    }

    if (!users?.emp_id) {
      mostrarError(
        "No se encontró la empresa del usuario."
      );

      return;
    }

    if (!categoria) {
      mostrarError(
        "No ha seleccionado una categoría."
      );

      return;
    }

    const itemNormalizado =
      String(item || "")
        .trim()
        .toUpperCase();

    if (!itemNormalizado) {
      mostrarError(
        "No ha ingresado un Item."
      );

      return;
    }

    /*
     * Validamos que la categoría seleccionada
     * siga existiendo dentro del catálogo activo.
     */
    const categoriaValida =
      categoriaOptions.some(
        (opcion) =>
          opcion.value ===
          categoria
      );

    if (!categoriaValida) {
      mostrarError(
        "La categoría seleccionada ya no está disponible."
      );

      return;
    }

    /*
     * Conservamos exactamente la validación existente:
     * el nombre no puede repetirse aunque cambie categoría.
     */
    const existe = leer.some(
      (registro) =>
        String(
          registro.nombre || ""
        )
          .trim()
          .toUpperCase() ===
        itemNormalizado
    );

    if (existe) {
      mostrarError(
        "Ya existe este Item."
      );

      return;
    }

    /*
     * REPUESTO y SERVICIO conservan
     * la lógica actual de precio.
     */
    const requierePrecio =
      categoria === "REPUESTO" ||
      categoria === "SERVICIO";

    if (
      requierePrecio &&
      price === ""
    ) {
      mostrarError(
        "Ingrese un precio de repuesto o servicio."
      );

      return;
    }

    const precioNumerico =
      requierePrecio
        ? Number(price)
        : 0;

    if (
      requierePrecio &&
      (!Number.isFinite(
        precioNumerico
      ) ||
        precioNumerico <= 0)
    ) {
      mostrarError(
        "Ingrese un precio válido mayor a cero."
      );

      return;
    }

    setBotonDisabled(true);

    try {
      const fechaOperacion =
        new Date();

      await ItemsSTDb({
        nombre:
          itemNormalizado,

        categoria,

        price:
          precioNumerico,

        userAdd:
          user?.email || "",

        userMod:
          user?.email || "",

        fechaAdd:
          fechaOperacion,

        fechaMod:
          fechaOperacion,

        emp_id:
          users.emp_id,
      });

      mostrarExito(
        "Item ingresado correctamente."
      );

      setItem("");
      setPrice("");

      /*
       * Conservamos categoría seleccionada
       * para facilitar ingreso consecutivo.
       */
      setFlag(
        (prev) => !prev
      );
    } catch (error) {
      console.error(
        "Error al ingresar Item ST:",
        error
      );

      mostrarError(
        "No fue posible ingresar el Item."
      );
    } finally {
      setBotonDisabled(false);
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Container
      fluid
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "24px 12px 40px",
      }}
    >
      <Container
        fluid
        style={{
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        {/* ===================================================
            CABECERA
        =================================================== */}

        <Segment
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            boxShadow:
              "0 4px 14px rgba(15,23,42,.05)",
            padding: "18px 20px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: "#ecfdf5",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "center",
              }}
            >
              <Icon
                name="boxes"
                color="teal"
                size="large"
                style={{ margin: 0 }}
              />
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 220,
              }}
            >
              <Header
                as="h2"
                style={{
                  margin: 0,
                  color: "#0f172a",
                }}
              >
                Gestión de Items ST

                <Header.Subheader
                  style={{
                    marginTop: 5,
                  }}
                >
                  Administra repuestos,
                  servicios, fallas, Test de
                  Ingreso y elementos de
                  garantía.
                </Header.Subheader>
              </Header>
            </div>

            <Button
              basic
              type="button"
              onClick={volver}
            >
              <Icon name="arrow left" />
              Volver
            </Button>
          </div>
        </Segment>

        {/* ===================================================
            ERROR CATÁLOGO
        =================================================== */}

        {errorCategorias && (
          <Message
            negative
            icon="exclamation triangle"
            header="Categorías no disponibles"
            content={
              errorCategorias
            }
          />
        )}

        {!cargandoCategorias &&
          !errorCategorias &&
          categoriaOptions.length ===
            0 && (
            <Message
              warning
              icon="settings"
              header="Categorías Ítems ST sin configurar"
              content="No existen categorías activas para Items ST. Debe configurarlas desde Administración de Catálogos."
            />
          )}

        {/* ===================================================
            1. NUEVO ITEM
        =================================================== */}

        <Segment
          style={{
            border:
              "2px solid #00b5ad",
            borderRadius: 12,
            boxShadow:
              "0 0 0 1px rgba(0,181,173,.08), 0 4px 14px rgba(15,23,42,.05)",
            marginBottom: 16,
          }}
        >
          <Header
            as="h3"
            style={{
              color: "#0f172a",
              marginBottom: 18,
            }}
          >
            <Icon
              name="plus square outline"
              color="teal"
            />

            <Header.Content>
              1. Nuevo Ítem ST

              <Header.Subheader>
                Ingresa un nuevo elemento al
                catálogo de Servicio Técnico.
              </Header.Subheader>
            </Header.Content>
          </Header>

          <Form
            size="small"
            onSubmit={
              handleSubmit
            }
          >
            <Form.Group
              widths="equal"
            >
              <Form.Dropdown
                label="Categoría"
                fluid
                search
                selection
                loading={
                  cargandoCategorias
                }
                disabled={
                  cargandoCategorias ||
                  categoriaOptions.length ===
                    0
                }
                placeholder="Selecciona Categoría"
                options={
                  categoriaOptions
                }
                value={
                  categoria
                }
                onChange={
                  handleDropdownChange
                }
              />

              {(categoria ===
                "REPUESTO" ||
                categoria ===
                  "SERVICIO") && (
                <Form.Input
                  fluid
                  label="Precio"
                  placeholder="Precio"
                  icon={{
                    name:
                      "dollar sign",
                    color: "teal",
                  }}
                  iconPosition="left"
                  value={price}
                  onChange={(e) => {
                    const value =
                      e.target.value;

                    if (
                      /^[1-9]\d*$/.test(
                        value
                      ) ||
                      value === ""
                    ) {
                      setPrice(
                        value
                      );
                    } else {
                      mostrarError(
                        "Ingrese un número positivo."
                      );
                    }
                  }}
                />
              )}

              <Form.Input
                fluid
                label="Nombre del ítem"
                placeholder="Nombre del Item"
                icon={{
                  name: "tag",
                  color: "teal",
                }}
                iconPosition="left"
                value={item}
                onChange={(e) =>
                  setItem(
                    e.target.value
                  )
                }
              />
            </Form.Group>

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                marginTop: 8,
              }}
            >
              <Button
                color="teal"
                type="submit"
                loading={
                  botonDisabled
                }
                disabled={
                  botonDisabled ||
                  cargandoCategorias ||
                  categoriaOptions.length ===
                    0
                }
              >
                <Icon name="plus" />
                Agregar ítem
              </Button>
            </div>
          </Form>
        </Segment>

        {/* ===================================================
            2. LISTADO
        =================================================== */}

        <Segment
          style={{
            border:
              "1px solid #e2e8f0",
            borderRadius: 12,
            boxShadow:
              "0 4px 14px rgba(15,23,42,.05)",
            padding: 0,
            overflow: "hidden",
          }}
        >
          {/* CABECERA LISTADO */}

          <div
            style={{
              padding: "16px",
              borderBottom:
                "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Header
              as="h3"
              style={{
                margin: 0,
                color: "#0f172a",
              }}
            >
              <Icon
                name="list alternate outline"
                color="teal"
              />

              <Header.Content>
                2. Ítems ST registrados

                <Header.Subheader>
                  Catálogo disponible para
                  los procesos de Servicio
                  Técnico.
                </Header.Subheader>
              </Header.Content>
            </Header>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Label
                basic
                color="teal"
              >
                Registros
                <Label.Detail>
                  {
                    filteredDataT.length
                  }
                </Label.Detail>
              </Label>

              <Button
                basic
                color="green"
                size="small"
                type="button"
                onClick={
                  ExportarXls
                }
                disabled={
                  filteredDataT.length ===
                  0
                }
              >
                <Icon name="file excel" />
                Exportar Excel
              </Button>
            </div>
          </div>

          {/* BUSCADOR */}

          <div
            style={{
              padding:
                "14px 16px",
              background:
                "#fff",
              borderBottom:
                "1px solid #e2e8f0",
            }}
          >
            <Input
              fluid
              icon={{
                name: "search",
                color: "teal",
              }}
              placeholder="Buscar por nombre, categoría u otro dato..."
              value={buscador}
              onChange={(e) =>
                setBuscador(
                  e.target.value
                )
              }
            />
          </div>

          {/* TABLA */}

          <div
            style={{
              maxHeight: 520,
              overflow: "auto",
            }}
          >
            <Table
              celled
              striped
              selectable
              compact="very"
              style={{
                margin: 0,
                minWidth: 800,
              }}
            >
              <Table.Header
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background:
                    "#f8fafc",
                }}
              >
                <Table.Row>
                  <Table.HeaderCell
                    width={1}
                    textAlign="center"
                  >
                    #
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Ítem
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={3}
                  >
                    Categoría
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={3}
                    textAlign="right"
                  >
                    Precio
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={2}
                    textAlign="center"
                  >
                    Acción
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {filteredDataT.map(
                  (
                    row,
                    index
                  ) => (
                    <Table.Row
                      key={row.id}
                    >
                      <Table.Cell
                        textAlign="center"
                      >
                        {index + 1}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace:
                            "normal",
                          fontWeight: 600,
                        }}
                      >
                        {row.nombre}
                      </Table.Cell>

                      <Table.Cell>
                        <Label
                          basic
                          size="tiny"
                          color="teal"
                        >
                          {row.categoria}
                        </Label>
                      </Table.Cell>

                      <Table.Cell
                        textAlign="right"
                        style={{
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {Number(
                          row.price ||
                            0
                        ) > 0
                          ? `$${Number(
                              row.price
                            ).toLocaleString(
                              "es-CL"
                            )}.-`
                          : "—"}
                      </Table.Cell>

                      <Table.Cell
                        textAlign="center"
                      >
                        <Button
                          as={Link}
                          to={`/serviciotecnico/actualizaitems/${row.id}`}
                          basic
                          compact
                          icon
                          size="mini"
                          color="teal"
                          title="Editar ítem"
                        >
                          <Icon
                            name="edit outline"
                            style={{
                              margin: 0,
                            }}
                          />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  )
                )}

                {filteredDataT.length ===
                  0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan="5"
                      textAlign="center"
                      style={{
                        padding: 35,
                        color:
                          "#64748b",
                      }}
                    >
                      <Icon
                        name="inbox"
                        size="large"
                        color="grey"
                      />

                      <div
                        style={{
                          marginTop: 8,
                        }}
                      >
                        No se encontraron
                        ítems.
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </Segment>

        {/* ===================================================
            ALERTAS
        =================================================== */}

        <Alertas
          tipo={alerta.tipo}
          mensaje={
            alerta.mensaje
          }
          estadoAlerta={
            estadoAlerta
          }
          cambiarEstadoAlerta={
            cambiarEstadoAlerta
          }
        />
      </Container>
    </Container>
  );
};

export default ItemsTest;